import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api, errorMessage } from '@/api/client';
import { useToast } from '@/components/toast-context';
import { Button, ErrorNote, TextField } from '@/components/ui';
import { MOCK } from '@/config';
import { AuthLayout, linkClass } from './AuthLayout';

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const f: Record<string, string> = {};
    if (password.length < 8) f.password = 'Use at least 8 characters.';
    if (confirm !== password) f.confirm = 'The passwords don’t match.';
    setFields(f);
    if (Object.keys(f).length) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/v1/auth/password/reset', { body: { token, password } });
      toast({ title: 'Password changed', description: 'Sign in with your new password.' });
      navigate('/sign-in', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout
        title="Reset link missing"
        footer={
          <Link to="/forgot-password" className={linkClass}>
            Ask for a new link
          </Link>
        }
      >
        <ErrorNote title="This link is incomplete" message="Open the link from the email again, or ask for a new one." />
        {MOCK && <p className="mt-4 text-xs text-muted-ink">Mock mode: try /reset-password?token=mock-reset-token</p>}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Choose a new password"
      footer={
        <Link to="/sign-in" className={linkClass}>
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        {error && <ErrorNote title="Couldn’t change the password" message={error} />}
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fields.password}
          hint="At least 8 characters."
          autoFocus
        />
        <TextField
          label="Repeat new password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={fields.confirm}
        />
        <Button type="submit" variant="primary" size="lg" loading={busy}>
          Change password
        </Button>
      </form>
    </AuthLayout>
  );
}
